import { useState } from 'react';
import { UMButton, UMIcon } from '../../urbanmap/_components';
import { DegreeField, DegreePair, DegreePairFields } from './DegreeFields';
import { RatioReadTable } from './RatioReadTable';
import { StationNotice } from './StationNotice';
import { fmt, isValidDjclim, isValidDju } from './domain';
import { parseNum } from './sheet';
import { PC, UMR, caption, card } from './tokens';
import type { DegreeDays, RatioTable as Ratios, WeatherStation } from './types';

interface Props {
  /** Station choisie à l'étape précédente — non modifiable ici. */
  station: WeatherStation;
  /** Degrés-jours retenus pour la station (standards ou corrigés). */
  degrees: DegreeDays;
  onDegreesChange: (next: DegreeDays) => void;
  onValidityChange: (ok: boolean) => void;
  onEditingChange?: (editing: boolean) => void;
  /** Mes ratios calibrés sur ces degrés-jours. */
  ratios: Ratios;
  /** Degrés-jours de mes ratios appliqués, pour afficher le coefficient de calibrage. */
  appliedDegrees: DegreeDays;
  /** Mes ratios ont déjà été appliqués à la station au moins une fois. */
  applied: boolean;
}

/** Encart « Mes ratios appliqués à mon projet » : DJ de la station (corrigibles) et ratios appliqués. */
export function ProjectRatiosCard(p: Props) {
  const [editing, setEditing] = useState(false);
  const [dju, setDju] = useState(String(p.degrees.dju));
  const [djclim, setDjclim] = useState(String(p.degrees.djclim));

  const djuValid = isValidDju(parseNum(dju));
  const climValid = isValidDjclim(parseNum(djclim));
  const overridden = p.degrees.dju !== p.station.dju || p.degrees.djclim !== p.station.djclim;
  const kHeat = p.appliedDegrees.dju > 0 ? p.degrees.dju / p.appliedDegrees.dju : 1;
  const kCool = p.appliedDegrees.djclim > 0 ? p.degrees.djclim / p.appliedDegrees.djclim : 1;

  const setMode = (on: boolean) => { setEditing(on); p.onEditingChange?.(on); };
  const open = () => { setDju(String(p.degrees.dju)); setDjclim(String(p.degrees.djclim)); setMode(true); };
  const editDju = (v: string) => {
    setDju(v);
    const n = parseNum(v);
    if (isValidDju(n)) p.onDegreesChange({ ...p.degrees, dju: n });
    p.onValidityChange(isValidDju(n) && climValid);
  };
  const editDjclim = (v: string) => {
    setDjclim(v);
    const n = parseNum(v);
    if (isValidDjclim(n)) p.onDegreesChange({ ...p.degrees, djclim: n });
    p.onValidityChange(djuValid && isValidDjclim(n));
  };
  const reset = () => {
    p.onDegreesChange({ dju: p.station.dju, djclim: p.station.djclim });
    setDju(String(p.station.dju)); setDjclim(String(p.station.djclim));
    p.onValidityChange(true);
  };

  return (
    <section style={{ ...card, padding: 0, display: 'flex', flexDirection: 'column', borderColor: p.applied ? PC.accentBorder : UMR.border }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 16px', background: p.applied ? PC.accentBg : UMR.grey100, borderBottom: `1px solid ${p.applied ? PC.accentBorder : UMR.border}` }}>
        <span style={{ width: 22, height: 22, background: UMR.white, border: `1px solid ${UMR.border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>3</span>
        <UMIcon name="thermostat" size={20} color={PC.accent} />
        <strong style={{ fontSize: 15 }}>Mes ratios appliqués à mon projet — {p.station.name}</strong>
        {overridden && (
          <span style={{ fontSize: 12, padding: '1px 6px', background: UMR.white, color: PC.accentStrong, border: `1px solid ${PC.accentBorder}` }}>DJ corrigés</span>
        )}
        <span style={{ flex: 1 }} />
        {editing
          ? <UMButton icon="check" disabled={!djuValid || !climValid} onClick={() => setMode(false)}>Terminer</UMButton>
          : <UMButton variant="secondary" icon="edit" onClick={open}>Éditer les DJ simulés</UMButton>}
        {overridden && <UMButton variant="subtle" icon="restart_alt" onClick={reset}>Réinitialiser la simulation</UMButton>}
      </header>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <StationNotice station={p.station} />
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {editing ? (
            <DegreePairFields>
              <DegreeField label="DJU simulés" text={dju} valid={djuValid} range={`hypothèse initiale ${fmt(p.station.dju)}`} onChange={editDju} />
              <DegreeField label="DJClim simulés" text={djclim} valid={climValid} range={`hypothèse initiale ${fmt(p.station.djclim)}`} onChange={editDjclim} />
            </DegreePairFields>
          ) : (
            <DegreePair
              dju={p.degrees.dju}
              djclim={p.degrees.djclim}
              hint={overridden ? `Hypothèses simulées initiales : ${fmt(p.station.dju)} / ${fmt(p.station.djclim)}` : 'Degrés-jours simulés — pas des normales source'}
              highlight={overridden}
            />
          )}
        </div>

        {!p.applied ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 16, background: UMR.grey100, border: `1px dashed ${UMR.border}`, color: UMR.textSecondary }}>
            <UMIcon name="table_rows" size={20} color={UMR.textSecondary} />
            <span style={{ fontSize: 13 }}>
              Aucun ratio appliqué pour l’instant. Utilisez le bouton « Appliquer mes ratios à mon
              projet » ci-dessus pour les calibrer sur {p.station.name} et voir leur détail ici.
            </span>
          </div>
        ) : (
          <>
            <div style={{ ...caption, padding: '6px 8px', background: UMR.grey100, border: `1px solid ${UMR.borderSubtle}` }}>
              Calibrage appliqué : chauffage × {fmt(kHeat, 2)} ({fmt(p.degrees.dju)} / {fmt(p.appliedDegrees.dju)} DJU) ·
              froid × {fmt(kCool, 2)} ({fmt(p.degrees.djclim)} / {fmt(p.appliedDegrees.djclim)} DJClim) · ECS inchangée.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <strong style={{ fontSize: 14 }}>Ratios appliqués, par typologie et période de construction</strong>
              <RatioReadTable value={p.ratios} stationName={p.station.name} />
            </div>
          </>
        )}
      </div>
    </section>
  );
}
